import React, { useState } from 'react';
import { Link } from 'react-router';
import { CostExplanationModal } from './CostExplanationModal';

/**
 * ActivityCard - Individual activity card with drag handle, icon, details and actions
 * Design: White card with shadow, 6-dot drag handle, yellow icon container
 */
export const ActivityCard = ({
  activity,
  editPath,
  onDelete,
  t,
  dragHandleProps = {}
}) => {
  const [showCostModal, setShowCostModal] = useState(false);

  const handleCostBadgeClick = (e) => {
    e.stopPropagation();
    setShowCostModal(true);
  };

  return (
    <>
      <div
        className="bg-white border rounded-4 p-3 shadow-sm"
        style={{ borderColor: '#f3f4f6' }}
      >
        <div className="d-flex align-items-start">
          {/* Drag Handle - 6-dot grid pattern */}
          <div
            className="drag-handle me-3 d-flex align-items-center"
            style={{ cursor: 'grab', paddingTop: '8px', touchAction: 'none' }}
            {...dragHandleProps}
          >
            <div className="d-flex flex-column gap-1">
              <div className="d-flex gap-1">
                <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px', opacity: 0.4 }}></span>
                <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px', opacity: 0.4 }}></span>
              </div>
              <div className="d-flex gap-1">
                <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px', opacity: 0.4 }}></span>
                <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px', opacity: 0.4 }}></span>
              </div>
              <div className="d-flex gap-1">
                <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px', opacity: 0.4 }}></span>
                <span className="rounded-circle bg-secondary" style={{ width: '4px', height: '4px', opacity: 0.4 }}></span>
              </div>
            </div>
          </div>

          {/* Activity Icon - Yellow background with border */}
          <div
            className="d-flex align-items-center justify-content-center me-3 flex-shrink-0"
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#fefce8',
              border: '1px solid #fef9c3',
              borderRadius: '12px'
            }}
          >
            {activity.icon_url ? (
              <img
                src={activity.icon_url}
                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                alt={activity.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<i class="bi bi-book text-warning" style="font-size: 24px;"></i>';
                }}
              />
            ) : (
              <i className="bi bi-book text-warning" style={{ fontSize: '24px' }}></i>
            )}
          </div>

          {/* Activity Details */}
          <div className="flex-grow-1 min-width-0">
            {/* Title Row with Action Buttons */}
            <div className="d-flex align-items-center justify-content-between mb-1">
              <h5 className="fw-bold mb-0 fs-6" style={{ color: '#111827' }}>{activity.name}</h5>
              {/* Action Buttons */}
              <div className="d-flex gap-1 flex-shrink-0">
                {editPath && (
                  <Link to={editPath} className="btn btn-sm btn-link text-secondary p-1">
                    <i className="bi bi-pencil"></i>
                  </Link>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-link text-secondary p-1"
                  onClick={onDelete}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>

            {/* Course Names */}
            {activity.course_names && (
              <p className="mb-2 small" style={{ color: '#6b7280', lineHeight: '1.4' }}>{activity.course_names}</p>
            )}

            {/* Activity Tags and Volume Badge */}
            {(() => {
              const hasVolumeBadge = activity.volume === 'moderate' || activity.volume === 'high';
              const filteredTags = activity.tags?.filter(tag => tag === 'Tốn thêm tiền' || tag === 'Cần máy tính bảng') || [];
              const hasTags = filteredTags.length > 0;

              if (!hasVolumeBadge && !hasTags) return null;

              return (
                <div className="d-flex gap-2 flex-wrap">
                  {/* Volume Badge */}
                  {hasVolumeBadge && (
                    <span
                      className="badge rounded-pill px-2 py-1 small d-inline-flex align-items-center"
                      style={{
                        backgroundColor: activity.volume === 'high' ? '#fef3c7' : '#ecfdf5',
                        border: `1px solid ${activity.volume === 'high' ? '#fcd34d' : '#a7f3d0'}`,
                        color: activity.volume === 'high' ? '#92400e' : '#047857',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                      data-testid={`volume-badge-${activity.activity_type}`}
                    >
                      <i className="bi bi-speedometer2 me-1" style={{ opacity: 0.7 }}></i>
                      {activity.volume === 'moderate' ? 'Vừa x2' : 'Cao x4'}
                    </span>
                  )}

                  {/* Cost and Tablet Tags */}
                  {filteredTags.map((tag) => {
                    const isCostTag = tag === 'Tốn thêm tiền';
                    const isTabletTag = tag === 'Cần máy tính bảng';

                    const tagStyle = isCostTag ? {
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#b91c1c'
                    } : {
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      color: '#1d4ed8'
                    };

                    return (
                      <span
                        key={tag}
                        className="badge rounded-pill px-2 py-1 small d-inline-flex align-items-center"
                        onClick={isCostTag ? handleCostBadgeClick : undefined}
                        style={{
                          cursor: isCostTag ? 'pointer' : 'default',
                          ...tagStyle,
                          fontWeight: 500,
                          fontSize: '12px'
                        }}
                        data-testid={isCostTag ? `cost-badge-${activity.activity_type}` : `tablet-badge-${activity.activity_type}`}
                      >
                        {isTabletTag && <i className="bi bi-tablet me-1" style={{ opacity: 0.7 }}></i>}
                        {tag}
                        {isCostTag && <i className="bi bi-info-circle ms-1" style={{ opacity: 0.7 }}></i>}
                      </span>
                    );
                  })}
                </div>
              );
            })()}

            {/* Metadata Tags */}
            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
              <div className="d-flex gap-2 flex-wrap mt-2">
                {Object.entries(activity.metadata).map(([key, value]) => (
                  <span key={key} className="badge bg-primary bg-opacity-10 text-primary small">
                    {formatMetadataKey(key)}: {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cost Explanation Modal */}
      {showCostModal && (
        <CostExplanationModal
          activityName={activity.name}
          activityType={activity.activity_type}
          preloadedAccount={activity.preloaded_account}
          onClose={() => setShowCostModal(false)}
        />
      )}
    </>
  );
};

// Helper function to format metadata key for display
const formatMetadataKey = (key) => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default ActivityCard;
